/* "Vraag feedback" (ADR 0021): de ping. Kies een Versie, personen, een vraag en eventueel een
 * deadline. Dat geeft per persoon een interne melding, een taak en een pushmelding op de iPhone
 * (GV Executive). Alleen interne teamleden; de database weigert iemand zonder CRM-rol. Er gaat
 * niets naar klanten, en de push zelf noemt geen document of klant, alleen wie vraagt, het
 * projectnummer, de versie en de deadline. */

import { useEffect, useMemo, useState } from 'react';
import { BellRing, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { DocumentGroup } from '../../../../supabase/functions/_shared/fileVersions';
import type { Bestand } from '../data/bestanden';
import { pingSamenvatting, type Mens } from '../data/feedback';
import { useMensen, useVraagFeedback } from '../hooks';
import { extensie } from './opmaak';

const ONTHOUD = 'gv.feedback.personen';

function leesOnthouden(): string[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(ONTHOUD) ?? '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function onthoud(ids: string[]) {
  try {
    window.localStorage.setItem(ONTHOUD, JSON.stringify(ids));
  } catch {
    /* privévenster of geblokkeerde opslag: dan kiest hij de volgende keer opnieuw */
  }
}

/** Testaccounts (@example.invalid) staan onderaan. */
const isTest = (m: Mens) => /\.invalid$/i.test(m.email);

function vandaag(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function VraagFeedback({ open, onOpenChange, projectId, projectNummer, doc, fileId }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectNummer: string | null;
  doc: DocumentGroup<Bestand> | null;
  /** Welke Versie vooraf gekozen is; zonder de nieuwste. */
  fileId?: string | null;
}) {
  const { toast } = useToast();
  const mensen = useMensen(open);
  const vraag = useVraagFeedback(projectId);
  const bestanden = useMemo(
    () => (doc?.versions ?? []).flatMap((v, i) => v.files.map((f) => ({ v, f, nieuwste: i === 0 }))),
    [doc],
  );
  const [gekozenBestand, setGekozenBestand] = useState<string>('');
  const [personen, setPersonen] = useState<Set<string>>(new Set());
  const [tekst, setTekst] = useState('');
  const [deadline, setDeadline] = useState('');
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setGekozenBestand(fileId && bestanden.some((b) => b.f.file.id === fileId) ? fileId : bestanden[0]?.f.file.id ?? '');
    setTekst('');
    setDeadline('');
    setFout(null);
  }, [open, fileId, bestanden]);

  const lijst = useMemo(
    () => [...(mensen.data ?? [])].sort((a, b) => Number(isTest(a)) - Number(isTest(b)) || Number(a.is_me) - Number(b.is_me) || a.name.localeCompare(b.name, 'nl')),
    [mensen.data],
  );
  useEffect(() => {
    if (!open || !mensen.data) return;
    const bekend = new Set(mensen.data.map((m) => m.user_id));
    setPersonen(new Set(leesOnthouden().filter((id) => bekend.has(id))));
  }, [open, mensen.data]);

  const keuze = bestanden.find((b) => b.f.file.id === gekozenBestand);
  const naam = (id: string) => mensen.data?.find((m) => m.user_id === id)?.name ?? 'iemand';

  const verstuur = () => {
    if (!doc || !keuze || personen.size === 0) return;
    setFout(null);
    const ids = [...personen];
    vraag.mutate(
      {
        versie: { file_id: keuze.f.file.id, file_name: keuze.f.file.name, folder: keuze.f.file.folder, version: keuze.v.version },
        titel: doc.title,
        vraag: tekst,
        deadline: deadline || null,
        personen: ids,
      },
      {
        onSuccess: (u) => {
          onthoud(ids);
          toast({ title: 'Feedback gevraagd', description: pingSamenvatting(u, naam) });
          onOpenChange(false);
        },
        onError: (e) => setFout((e as Error).message),
      },
    );
  };

  const meerdereVarianten = (doc?.versions ?? []).some((v) => v.files.length > 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="h-5 w-5" aria-hidden /> Vraag feedback</DialogTitle>
          <DialogDescription className="break-words">{doc?.title ?? 'Document'}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="feedback-versie">Versie</Label>
            <select
              id="feedback-versie"
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={gekozenBestand}
              onChange={(e) => setGekozenBestand(e.target.value)}
            >
              {bestanden.map(({ v, f, nieuwste }) => (
                <option key={f.file.id} value={f.file.id}>
                  v{v.version}{meerdereVarianten ? ` · ${f.parsed.lang ?? extensie(f.file.name).toUpperCase()}` : ''}{v.concept ? ' · concept' : ''}{nieuwste ? ' (nieuwste)' : ''}
                </option>
              ))}
            </select>
          </div>

          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">Aan wie</legend>
            {mensen.isPending ? (
              <div className="h-16 animate-pulse rounded-md bg-muted" aria-busy />
            ) : lijst.length === 0 ? (
              <p className="text-sm text-muted-foreground">Geen teamleden gevonden.</p>
            ) : (
              <ul className="flex flex-col divide-y rounded-md border">
                {lijst.map((m) => {
                  const aan = personen.has(m.user_id);
                  return (
                    <li key={m.user_id}>
                      <label className={cn('flex cursor-pointer items-center gap-3 px-3 py-2 text-sm', aan && 'bg-muted/60')}>
                        <Checkbox
                          checked={aan}
                          onCheckedChange={(c) => {
                            const n = new Set(personen);
                            if (c === true) n.add(m.user_id); else n.delete(m.user_id);
                            setPersonen(n);
                          }}
                        />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="font-medium">{m.name}{m.is_me ? ' (jij)' : ''}</span>
                          <span className="truncate text-xs text-muted-foreground">{m.email}</span>
                        </span>
                        {m.has_device ? (
                          <span className="flex items-center gap-1 whitespace-nowrap text-xs text-emerald-700"><Smartphone className="h-3.5 w-3.5" aria-hidden /> iPhone</span>
                        ) : (
                          <span className="whitespace-nowrap text-xs text-muted-foreground">geen iPhone</span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="feedback-vraag">Vraag <span className="font-normal text-muted-foreground">(optioneel)</span></Label>
            <Textarea
              id="feedback-vraag"
              value={tekst}
              onChange={(e) => setTekst(e.target.value)}
              placeholder="Waar moeten ze vooral naar kijken?"
              rows={3}
              maxLength={2000}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="feedback-deadline">Deadline <span className="font-normal text-muted-foreground">(optioneel)</span></Label>
            <Input id="feedback-deadline" type="date" min={vandaag()} value={deadline} onChange={(e) => setDeadline(e.target.value)} className="w-44" />
          </div>

          <p className="m-0 flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-foreground" aria-hidden />
            <span>
              Alleen interne teamleden; er gaat niets naar klanten. Ieder krijgt een melding in het CRM, een taak
              {deadline ? ' met deze deadline' : ''} en, met een aangemelde iPhone, een pushmelding. De push noemt
              alleen wie het vraagt{projectNummer ? `, ${projectNummer}` : ''} en de versie, niet het document of de klant.
            </span>
          </p>

          {fout && <p role="alert" className="m-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{fout}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={verstuur} disabled={!keuze || personen.size === 0 || vraag.isPending}>
            {vraag.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Vraag feedback{personen.size ? ` (${personen.size})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
